import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import BarcodeCameraView from '../../components/camera/BarcodeCameraView';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import { Dialog } from '../../components';
import { EmptyState } from '../../design/components';
import Text from '../../components/text/Text';

import { createStocktakeDetailStyles } from './StocktakeDetail.styles';

const StocktakeScannerDialog = ({
	isOpen,
	onClose,
	onBarcodeScanned,
}) => {
	const styles = useThemedStyles(createStocktakeDetailStyles);
	const [missedBarcode, setMissedBarcode] = useState(null);

	// A toast would sit behind this dialog on iOS, so a missed scan is reported inside it.
	const handleScanned = useCallback((barcode) => {
		setMissedBarcode(onBarcodeScanned(barcode) ? null : barcode);
	}, [onBarcodeScanned]);

	const {
		cameraPermission,
		requestCameraPermission,
		handleBarcodeScanned,
		resetScanner,
	} = useBarcodeScanner({
		onScanned: handleScanned,
		// Match the stocktake's saved codes, including case and internal barcodes.
		validateEAN: false,
	});

	useEffect(() => {
		if (isOpen) {
			requestCameraPermission();
		}
	}, [isOpen, requestCameraPermission]);

	useEffect(() => {
		if (!isOpen) {
			resetScanner();
			setMissedBarcode(null);
		}
	}, [isOpen, resetScanner]);

	const renderScanner = () => {
		if (!cameraPermission) {
			return <Text style={[styles.dialogMeta, styles.scannerHint]}>Requesting camera permission…</Text>;
		}

		if (!cameraPermission.granted) {
			return (
				<EmptyState
					icon="camera-off"
					title="Camera access is required to scan barcodes."
					actionLabel="Grant permission"
					onAction={requestCameraPermission}
				/>
			);
		}

		return (
			<BarcodeCameraView
				style={styles.scannerCamera}
				barcodeScannerSettings={{ barcodeTypes: ['ean13', 'ean8', 'upc_a', 'upc_e', 'itf14', 'code128', 'code39', 'code93', 'codabar', 'qr', 'datamatrix', 'pdf417', 'aztec'] }}
				onBarcodeScanned={handleBarcodeScanned}
			/>
		);
	};

	return (
		<Dialog
			isOpen={isOpen}
			onClose={onClose}
			noPadding={true}
		>
			<View style={styles.scannerContainer}>
				{renderScanner()}
				{missedBarcode ? (
					<Text style={[styles.scannerHint, styles.scannerMiss]}>
						No product in this stocktake has barcode {missedBarcode}.
					</Text>
				) : (
					<Text style={styles.scannerHint}>
						Align a barcode within the frame to update its batches.
					</Text>
				)}
			</View>
		</Dialog>
	);
};

export default StocktakeScannerDialog;
